/* eslint-disable formatjs/no-literal-string-in-jsx */
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { batch, useDispatch } from 'react-redux';
import { Logger } from '@saviynt/common';
import {
  ApplicationIcon,
  Icon,
  ListRow,
  ListRowGroup,
  Menu,
  MenuDragAndDrop,
  type MenuOption,
  Tooltip,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import {
  CF_NEU_BLD,
  CF_SEC_BLD,
  F_KEY_NM,
  F_KEY_ON,
  F_KEY_SA,
  F_KEY_ST,
  F_KEY_TG,
  l,
  LIST_COLUMNS,
  LIST_MAX_COUNT,
  LIST_SORT_OPTIONS,
  type MessageState,
} from '../../../../shared/constants';
import { useInViewObserver, useTypedSelector } from '../../../../shared/hooks';
import {
  type ServiceAccountOwners,
  type ServiceIdentityItem,
  type ServiceIdentitySortField,
  type ServiceIdentitySortOrder,
} from '../../../../shared/types';
import { useGetIdentityListMutation } from '../../data-layer/serviceIdentityApi';
import { serviceIdentityActions } from '../../data-store/serviceIdentitySlice';
import { getStatusKind } from '../../utils';
import { ServiceIdentityListAction } from '../ServiceIdentityListAction';

import {
  EmptyList,
  EmptyListWithFilters,
  InitialError,
  InitialLoading,
  NextPageError,
  NextPageLoader,
} from './ServiceIdentityListShared';

import './ServiceIdentityList.css';

interface ServiceIdentityTracker {
  id: number;
  isRowDetailsOpen: boolean;
  isTransition?: boolean;
}

interface TrackedServiceIdentityItem extends ServiceIdentityItem {
  tracker: ServiceIdentityTracker;
}

/**
 * Displays a paginated, sortable list of service identity accounts with
 * filtering, infinite scroll, row-detail expansion, and side panel support.
 * Reads search, filter, and sort state from the Redux store.
 *
 * @returns The rendered service identity list, or a contextual empty, error, or loading state.
 */
function ServiceIdentityList() {
  // Hooks
  const intl = useIntl();

  // States
  const abortRef = useRef<((reason: string) => void) | null>(null);

  const [identityList, setIdentityList] = useState<
    TrackedServiceIdentityItem[]
  >([]);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [isListError, setIsListError] = useState(false);
  const [isObserverInView, setIsObserverInView] = useState(true);

  // Column picker states
  const [columnOptions, setColumnOptions] = useState(
    LIST_COLUMNS.filter((lc) => lc.value !== F_KEY_NM)
  );
  const [isPickerExpanded, setIsPickerExpanded] = useState(false);

  // Hooks
  const dispatch = useDispatch();
  const {
    searchText,
    activeFilters,
    sort,
    order,
    isGlobalLoading,
    list: { totalCount, rowsToUpdate },
    sidePanel: { isSidePanelOpen, sidePanelData },
  } = useTypedSelector((state) => state.savicopilot.serviceIdentity);

  const listObserverElement = useInViewObserver(({ isInView }) => {
    setIsObserverInView(isInView);
  });

  const [fetchIdentityList, { isLoading: isListLoading }] =
    useGetIdentityListMutation();

  // Constants
  const isValidApiCall = useMemo(
    () =>
      Boolean(
        totalCount === null ||
          (currentOffset < totalCount && identityList.length < totalCount)
      ),
    [currentOffset, totalCount, identityList.length]
  );
  const isListZero = useMemo(
    () => Boolean(identityList.length === 0),
    [identityList.length]
  );
  const isZeroCount = useMemo(
    () => Boolean(isListZero && totalCount !== null && !isValidApiCall),
    [totalCount, isListZero, isValidApiCall]
  );
  const isFilterApplied = useMemo(
    () =>
      searchText.length > 0 ||
      Object.values(activeFilters).some((oa) => oa.length > 0),
    [searchText, activeFilters]
  );

  // Effects
  useEffect(() => {
    dispatch(serviceIdentityActions.setIsListLoading(isListLoading));
  }, [dispatch, isListLoading]);

  // Utils
  const handleListApiCall = useCallback(
    async (offset: number) => {
      abortRef.current?.('New Search');

      const payload = {
        search: searchText,
        filter: activeFilters,
        offset,
        max: LIST_MAX_COUNT,
        sort,
        order,
      };

      try {
        const controller = fetchIdentityList(payload);

        abortRef.current = () => controller.abort();

        const response = await controller.unwrap();

        return response;
      } catch (error) {
        if ((error as { name?: string }).name !== 'AbortError') {
          setIsListError(true);
          setIsObserverInView(false);

          throw error;
        }

        throw Error('AbortError');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeFilters, order, searchText, sort, fetchIdentityList]
  );

  // Infinite scroll effect
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (!isObserverInView || !isValidApiCall) {
        return;
      }

      Logger.log(
        'Fetching',
        currentOffset,
        'to',
        currentOffset + LIST_MAX_COUNT
      );

      handleListApiCall(currentOffset)
        .then((response) => {
          setIsObserverInView(false);
          setCurrentOffset((prev) => prev + LIST_MAX_COUNT);
          setIsListError(false);

          const { total, data } = response as {
            total: number;
            data: ServiceIdentityItem[] | null | undefined;
          };

          if (!data) {
            dispatch(serviceIdentityActions.setListTotalCount(0));

            return;
          }

          setIdentityList((prev) => [
            ...prev,
            ...data.map((i) => ({
              ...i,
              tracker: {
                id: i.id,
                isRowDetailsOpen: false,
                isTransition: false,
              },
            })),
          ]);
          dispatch(serviceIdentityActions.setListTotalCount(total));
        })
        .catch((error) => {
          Logger.log('Error fetching list: ', error);
        });
    }, 10);

    return () => clearTimeout(debounceTimer);
  }, [
    dispatch,
    isObserverInView,
    isValidApiCall,
    currentOffset,
    activeFilters,
    handleListApiCall,
  ]);

  // Resetting list and offset to default so that observer is visible
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (!isGlobalLoading) {
        batch(() => {
          dispatch(serviceIdentityActions.setListTotalCount(null));
          dispatch(serviceIdentityActions.setIsSidePanelOpen(false));
          dispatch(serviceIdentityActions.setSidePanelData(null));
        });
        setIdentityList([]);
        setIsListError(false);
        setCurrentOffset(0);
        setIsObserverInView(true);
      }
    }, 20);

    return () => clearTimeout(debounceTimer);
  }, [
    // Add all the dependencies based on which you want to refresh the page
    dispatch,
    isGlobalLoading,
    activeFilters,
    order,
    searchText,
    sort,
  ]);

  // Sidepanel handling and row connector logic on `row` click
  const handleRowClick = useCallback(
    (id: number) => {
      let targetRow: ServiceIdentityItem;

      // Set isRowDetailsOpen only on the clicked row, reset all others
      const listRows = identityList.map((i) => {
        if (i.tracker.id === id) {
          targetRow = i;

          return {
            ...i,
            tracker: {
              ...i.tracker,
              isRowDetailsOpen: true,
            },
          };
        }

        return {
          ...i,
          tracker: {
            ...i.tracker,
            isRowDetailsOpen: false,
          },
        };
      });

      setIdentityList(listRows);

      batch(() => {
        dispatch(serviceIdentityActions.setSidePanelData(targetRow));
        dispatch(serviceIdentityActions.setIsSidePanelOpen(true));
      });
    },
    [dispatch, identityList]
  );

  const handleColumnPickerChange = (co: MenuOption) => {
    if (co.value === F_KEY_ST && !co.isChecked) {
      dispatch(
        serviceIdentityActions.setSort({
          sort: F_KEY_NM,
          order: 'asc',
        })
      );
    }
  };

  // Update rows once the identity is saved
  useEffect(() => {
    if (!rowsToUpdate) {
      return;
    }

    setIdentityList((prev) =>
      prev.map((i) =>
        i.id in rowsToUpdate ? { ...i, ...rowsToUpdate[i.id] } : i
      )
    );
  }, [dispatch, rowsToUpdate]);

  // Render
  if (totalCount === null && isListError) {
    return <InitialError onRefresh={() => setIsListError(false)} />;
  }

  if (totalCount === null) {
    return <InitialLoading />;
  }

  if (isZeroCount && isFilterApplied) {
    return <EmptyListWithFilters />;
  }

  if (isZeroCount) {
    return <EmptyList />;
  }

  return (
    <div className={cx('ServiceIdentityList-listWrapper')}>
      <ListRowGroup className={cx('ServiceIdentityList-container')}>
        <ListRow
          className={cx(
            'ServiceIdentityList-listRow',
            'ServiceIdentityList-listRow--isNotConnected',
            'ServiceIdentityList-listHeader--isHeader'
          )}
          classNames={{
            contentWrapperClassName: 'ServiceIdentityList-centerContentWrapper',
          }}
          leadingComp={<div className='ServiceIdentityList-listHeaderFiller' />}
          centerContent={
            <div className='ServiceIdentityList-listHeaderCenterContent'>
              {LIST_COLUMNS.sort(
                (a, b) =>
                  columnOptions.findIndex((o) => o.value === a.value) -
                  columnOptions.findIndex((o) => o.value === b.value)
              )
                .filter(
                  (lc) =>
                    columnOptions.find((_lc) => _lc.value === lc.value)
                      ?.isChecked ?? lc.value === F_KEY_NM
                )
                .map((lc) => {
                  if (lc.isSortField) {
                    const modifiedSortOptions = LIST_SORT_OPTIONS.map((so) => ({
                      ...so,
                      isChecked: sort === lc.value && order === so.value,
                    }));
                    const activeSortOption = modifiedSortOptions.find(
                      (ms) => ms.isChecked
                    );

                    return (
                      <div
                        key={lc.value}
                        className='ServiceIdentityList-listSortableHeader'>
                        <Menu
                          label={intl.formatMessage(
                            l[lc.label as MessageState]
                          )}
                          options={modifiedSortOptions.map((mo) => ({
                            ...mo,
                            label: intl.formatMessage(
                              l[mo.label as MessageState]
                            ),
                          }))}
                          onChange={(so: MenuOption) => {
                            dispatch(
                              serviceIdentityActions.setSort({
                                sort: lc.value as ServiceIdentitySortField,
                                order: so.value as ServiceIdentitySortOrder,
                              })
                            );
                          }}
                          isPortalEnabled
                          className='ServiceIdentityList-sortableHeaderMenu'
                          classNames={{
                            triggerClassName:
                              'ServiceIdentityList-sortableHeaderMenuTrigger',
                          }}
                        />
                        {activeSortOption && (
                          <div className='ServiceIdentityList-listSortableHeaderIcon'>
                            <Icon
                              kind={activeSortOption.icon}
                              size='smallMedium'
                            />
                          </div>
                        )}
                      </div>
                    );
                  }

                  return (
                    <div
                      key={lc.value}
                      className='ServiceIdentityList-listHeaderTitle'>
                      <Typography kind='body3' color={CF_NEU_BLD}>
                        {intl.formatMessage(l[lc.label as MessageState])}
                      </Typography>
                    </div>
                  );
                })}
            </div>
          }
          trailingContent={
            !isSidePanelOpen ? (
              <div className='ServiceIdentityList-listHeaderColumnPicker'>
                <MenuDragAndDrop
                  label={intl.formatMessage({
                    id: 'z0Qqz8',
                    defaultMessage: 'Show, hide, and reorder columns',
                    description: 'Show, hide, and reorder columns',
                  })}
                  options={columnOptions.map((co) => {
                    const newLabel = LIST_COLUMNS.find(
                      (lc) => lc.value === co.value
                    )?.label.replace('sort.', '') as MessageState;

                    return {
                      ...co,
                      label: intl.formatMessage(l[newLabel]),
                    };
                  })}
                  setOptions={setColumnOptions}
                  trigger={<Icon kind='Settings' size='medium' />}
                  isExpanded={isPickerExpanded}
                  setIsExpanded={setIsPickerExpanded}
                  onChange={handleColumnPickerChange}
                  isPortalEnabled
                />
              </div>
            ) : undefined
          }
          isCheckboxDisabled
        />
        {identityList.map((i) => {
          const isRowDetailsOpen = Boolean(
            sidePanelData !== null && sidePanelData.id === i.id
          );
          const { statusColor, statusTextKey } = getStatusKind(i.status);
          const rankGroupedOwners = Object.entries(
            i.owners.reduce<Record<number, ServiceAccountOwners[]>>(
              (acc, item) => {
                if (!(item.rank in acc)) {
                  acc[item.rank] = [];
                }

                acc[item.rank].push(item);

                return acc;
              },
              {}
            )
          );

          return (
            <ListRow
              key={i.id}
              className={cx(
                'ServiceIdentityList-listRow',
                isRowDetailsOpen
                  ? 'ServiceIdentityList-listRow--isConnected'
                  : 'ServiceIdentityList-listRow--isNotConnected',
                i.tracker.isTransition &&
                  'ServiceIdentityList-listRow--isTransition'
              )}
              classNames={{
                contentWrapperClassName:
                  'ServiceIdentityList-centerContentWrapper',
              }}
              leadingComp={
                <ApplicationIcon icon='ServiceAccount' size='large' />
              }
              centerContent={
                <div className='ServiceIdentityList-listRowCenterContent'>
                  <div className='ServiceIdentityList-listRowTitle'>
                    <Typography
                      kind='body1-bold'
                      lineLimit={1}
                      tooltipConfigs={{ isPortalEnabled: true }}
                      color={CF_SEC_BLD}
                      isNoWrap>
                      {i.name}
                    </Typography>
                    <Typography
                      kind='body3'
                      lineLimit={1}
                      tooltipConfigs={{ isPortalEnabled: true }}
                      color={CF_SEC_BLD}
                      isNoWrap>
                      {i.description}
                    </Typography>
                  </div>
                  {columnOptions
                    .filter((lc) => lc.isChecked && lc.value !== F_KEY_NM)
                    .map((lc) => {
                      if (lc.value === F_KEY_ST) {
                        return (
                          <div
                            key={lc.value}
                            className='ServiceIdentityList-listRowValue'>
                            <Typography kind='body2' color={statusColor}>
                              {`● ${intl.formatMessage(l[statusTextKey])}`}
                            </Typography>
                          </div>
                        );
                      }

                      if (lc.value === F_KEY_ON) {
                        return (
                          <div
                            key={lc.value}
                            className='ServiceIdentityList-listRowValue'>
                            {rankGroupedOwners.length > 0 ? (
                              <Tooltip
                                isPortalEnabled
                                className='ServiceIdentityList-listRow-Tooltip'
                                placement='bottom'
                                trigger={
                                  <Typography
                                    kind='body2'
                                    lineLimit={2}
                                    color={CF_SEC_BLD}
                                    tooltipConfigs={{ isPortalEnabled: true }}>
                                    {rankGroupedOwners.map(
                                      ([rank, owners], index) => {
                                        const isRank1InactiveOnwer = Boolean(
                                          owners.length > 0 &&
                                            String(rank) === '1' &&
                                            owners.some(
                                              (o) =>
                                                o.type.toLowerCase() ===
                                                  'user' &&
                                                (
                                                  o.status as string | undefined
                                                )?.toLowerCase() !== 'active'
                                            )
                                        );

                                        return (
                                          <React.Fragment key={rank}>
                                            <span
                                              className={cx(
                                                'ServiceIdentityList-ownerRankCell',
                                                isRank1InactiveOnwer &&
                                                  'ServiceIdentityList-ownerRankCell--isCritical'
                                              )}>
                                              <FormattedMessage
                                                id='kzR59h'
                                                defaultMessage='Rank {rank}:'
                                                description='Rank holder'
                                                values={{ rank }}
                                              />
                                              &nbsp;
                                            </span>
                                            {owners
                                              .map(
                                                (o) =>
                                                  `${o.firstname ?? ''} ${
                                                    o.lastname ?? ''
                                                  } (${o.name})`
                                              )
                                              .join(', ')}
                                            {index !==
                                              rankGroupedOwners.length - 1 && (
                                              <br />
                                            )}
                                          </React.Fragment>
                                        );
                                      }
                                    )}
                                  </Typography>
                                }
                                text={
                                  <>
                                    {rankGroupedOwners.map(
                                      ([rank, owners], index) => (
                                        <React.Fragment key={rank}>
                                          <span className='ServiceIdentityList-ownerRankCell'>
                                            <FormattedMessage
                                              id='kzR59h'
                                              defaultMessage='Rank {rank}:'
                                              description='Rank holder'
                                              values={{ rank }}
                                            />
                                            &nbsp;
                                          </span>
                                          {owners
                                            .map(
                                              (o) =>
                                                `${o.firstname ?? ''} ${
                                                  o.lastname ?? ''
                                                } (${o.name})`
                                            )
                                            .join(', ')}
                                          {index !==
                                            rankGroupedOwners.length - 1 && (
                                            <br />
                                          )}
                                        </React.Fragment>
                                      )
                                    )}
                                  </>
                                }
                              />
                            ) : (
                              <Typography kind='body2' color={CF_SEC_BLD}>
                                <FormattedMessage
                                  id='YoUJI1'
                                  defaultMessage='-'
                                  description='Hyphen'
                                />
                              </Typography>
                            )}
                          </div>
                        );
                      }

                      if (lc.value === F_KEY_TG) {
                        return (
                          <div
                            key={lc.value}
                            className='ServiceIdentityList-listRowValue'>
                            <Typography
                              kind='body2'
                              lineLimit={2}
                              color={CF_SEC_BLD}
                              tooltipConfigs={{ isPortalEnabled: true }}>
                              {Array.isArray(i.tags) && i.tags.length > 0 ? (
                                i.tags.join(', ')
                              ) : (
                                <FormattedMessage
                                  id='YoUJI1'
                                  defaultMessage='-'
                                  description='Hyphen'
                                />
                              )}
                            </Typography>
                          </div>
                        );
                      }

                      if (lc.value === F_KEY_SA) {
                        return (
                          <div
                            key={lc.value}
                            className='ServiceIdentityList-listRowValue'>
                            {i.serviceAccounts.length > 0 ? (
                              <Tooltip
                                isPortalEnabled
                                className='ServiceIdentityList-listRow-Tooltip'
                                placement='bottom'
                                trigger={
                                  <Typography
                                    kind='body2'
                                    lineLimit={2}
                                    color={CF_SEC_BLD}
                                    tooltipConfigs={{ isPortalEnabled: true }}>
                                    {i.serviceAccounts
                                      .map((sa) => ({
                                        key: sa.accountKey,
                                        name: sa.accountName,
                                      }))
                                      .sort((a, b) =>
                                        a.name
                                          .toLowerCase()
                                          .localeCompare(b.name.toLowerCase())
                                      )
                                      .map(({ key, name }, index) => (
                                        <React.Fragment key={key}>
                                          {name}
                                          {index !==
                                            i.serviceAccounts.length - 1 && (
                                            <br />
                                          )}
                                        </React.Fragment>
                                      ))}
                                  </Typography>
                                }
                                text={
                                  <>
                                    {i.serviceAccounts
                                      .map((sa) => ({
                                        key: sa.accountKey,
                                        name: sa.accountName,
                                      }))
                                      .sort((a, b) =>
                                        a.name
                                          .toLowerCase()
                                          .localeCompare(b.name.toLowerCase())
                                      )
                                      .map(({ key, name }, index, arr) => (
                                        <React.Fragment key={key}>
                                          {name}
                                          {index !== arr.length - 1 && <br />}
                                        </React.Fragment>
                                      ))}
                                  </>
                                }
                              />
                            ) : (
                              <Typography kind='body2' color={CF_SEC_BLD}>
                                <FormattedMessage
                                  id='YoUJI1'
                                  defaultMessage='-'
                                  description='Hyphen'
                                />
                              </Typography>
                            )}
                          </div>
                        );
                      }

                      return null;
                    })}
                </div>
              }
              trailingContent={
                !isSidePanelOpen &&
                !i.status.toLowerCase().includes('delete') ? (
                  <ServiceIdentityListAction
                    serviceIdentityId={i.id}
                    policyId={i.passwordPolicy?.id}
                    isLoading={isListLoading}
                    isDisabled={i.status.toLowerCase() === 'disabled'}
                    serviceAccounts={i.serviceAccounts}
                    currentOwners={i.owners}
                  />
                ) : (
                  <div className='ServiceIdentityList-listRowFiller' />
                )
              }
              handleRowClick={() => {
                handleRowClick(i.id);
              }}
              isRowDetailsOpen={isRowDetailsOpen}
              isCheckboxDisabled
              connectorDistance='25px'
              isRowConnectTransitionEnabled
            />
          );
        })}
        {/* Observer Element */}
        {!isListError ? listObserverElement : null}
        {/* Loading Skeleton */}
        {!isListError && isValidApiCall && <NextPageLoader />}
        {/* Retry mechanism */}
        {isListError && (
          <NextPageError onNextRefresh={() => setIsListError(false)} />
        )}
      </ListRowGroup>
    </div>
  );
}

export default memo(ServiceIdentityList);
